/**
 * TabScreenWrapper - Reusable wrapper for tab screen content
 *
 * Handles responsive top and bottom padding to prevent content from being
 * cut by browser UI (mobile) or hidden behind the bottom tab bar.
 *
 * Responsive Spacing Strategy:
 * - Mobile web (< 768px): Extra padding for Chrome UI + tab groups
 * - Desktop web (>= 768px): Minimal padding (original values)
 * - Native apps: Standard padding
 *
 * Usage:
 * ```tsx
 * <TabScreenWrapper>
 *   <ScrollView>...</ScrollView>
 * </TabScreenWrapper>
 * ```
 *
 * Or with ScrollView variant:
 * ```tsx
 * <TabScreenWrapper.ScrollView>
 *   <Content />
 * </TabScreenWrapper.ScrollView>
 * ```
 *
 * @see https://reactnavigation.org/docs/bottom-tab-navigator/#useBottomTabBarHeight
 */
import { View, ScrollView as RNScrollView, type ScrollViewProps, type ViewProps, Platform, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { forwardRef } from 'react';

/**
 * Tab bar height defined in _layout.tsx (line 52)
 * This is the height of the glassmorphism bottom tab bar
 */
const TAB_BAR_HEIGHT = 60;

/**
 * Additional padding to ensure content doesn't touch the tab bar
 * Provides visual breathing room between content and navigation
 */
const CONTENT_PADDING = 16;

/**
 * Responsive breakpoint for mobile vs desktop detection
 * Values below this are considered mobile, above are desktop
 */
const MOBILE_BREAKPOINT = 768;

interface TabScreenWrapperProps extends ViewProps {
  children: React.ReactNode;
}

/**
 * Base wrapper component that adds responsive top and bottom padding
 * Use this when you have your own ScrollView or FlatList inside
 */
export function TabScreenWrapper({ children, style, ...props }: TabScreenWrapperProps) {
  const { topPadding, bottomPadding } = useResponsiveScreenPadding();

  return (
    <View
      style={[{ flex: 1, paddingTop: topPadding, paddingBottom: bottomPadding }, style]}
      {...props}
    >
      {children}
    </View>
  );
}

interface TabScreenScrollViewProps extends ScrollViewProps {
  children: React.ReactNode;
  /** Additional className for contentContainerStyle (merged with padding) */
  contentContainerClassName?: string;
}

/**
 * ScrollView variant with built-in responsive top and bottom padding
 * Handles contentContainerStyle correctly for scrollable content
 */
const TabScreenScrollView = forwardRef<RNScrollView, TabScreenScrollViewProps>(
  ({ children, contentContainerStyle, contentContainerClassName, ...props }, ref) => {
    const { topPadding, bottomPadding } = useResponsiveScreenPadding();

    return (
      <RNScrollView
        ref={ref}
        contentContainerStyle={[
          { paddingTop: topPadding, paddingBottom: bottomPadding },
          contentContainerStyle,
        ]}
        {...(contentContainerClassName && { contentContainerClassName })}
        {...props}
      >
        {children}
      </RNScrollView>
    );
  }
);

TabScreenScrollView.displayName = 'TabScreenWrapper.ScrollView';

// Attach ScrollView variant to main component
TabScreenWrapper.ScrollView = TabScreenScrollView;

/**
 * Hook to get responsive top and bottom padding for screen content
 *
 * Returns different padding values based on viewport size:
 * - Mobile web (< 768px): Extra padding for Chrome UI + tab groups
 * - Desktop web (>= 768px): Minimal padding
 * - Native apps: Standard mobile padding
 *
 * @returns {{ topPadding: number, bottomPadding: number }}
 *
 * Usage:
 * ```tsx
 * const { topPadding, bottomPadding } = useResponsiveScreenPadding();
 * <ScrollView contentContainerStyle={{ paddingTop: topPadding, paddingBottom: bottomPadding }} />
 * ```
 */
export function useResponsiveScreenPadding(): { topPadding: number; bottomPadding: number } {
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();

  // Determine if mobile viewport
  const isMobile = Platform.OS !== 'web' || width < MOBILE_BREAKPOINT;

  // Top padding: Extra space for mobile browser UI (Chrome address bar + tab groups)
  // Mobile: 60px (Chrome UI space), Desktop: 24px (minimal header space)
  const topPadding = isMobile ? 60 + insets.top : 24 + insets.top;

  // Bottom padding: Tab bar + content padding + extra space for mobile browser UI
  // Mobile: 200px total (accounts for tab bar + Chrome UI), Desktop: 100px
  const baseBottomPadding = isMobile ? 200 : 100;
  const bottomPadding = baseBottomPadding + insets.bottom;

  return { topPadding, bottomPadding };
}

/**
 * Legacy hook for backward compatibility
 * Returns only bottom padding (use useResponsiveScreenPadding for full control)
 *
 * @deprecated Use useResponsiveScreenPadding() instead for responsive top + bottom padding
 */
export function useTabScreenPadding(): number {
  const { bottomPadding } = useResponsiveScreenPadding();
  return bottomPadding;
}

// Export constants for direct usage if needed
export { TAB_BAR_HEIGHT, CONTENT_PADDING };
